import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './AdminPanel.module.css';
import { getHostIP } from '../../networking/Hosts';

const getVaccineLabel = (vaccineType) => {
    if (vaccineType === 'HPV') return 'HPV';
    if (vaccineType === 'CRC') return '肠癌';
    if (vaccineType === 'RSV') return 'RSV';
    return '流感';
};

const getVaccineClass = (vaccineType) => String(vaccineType || 'FLU').toLowerCase();

const AdminPanel = () => {
    // 管理员认证状态
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [adminTokenInput, setAdminTokenInput] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [adminToken, setAdminToken] = useState('');

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionDetails, setSessionDetails] = useState(null);
    
    // 分页状态
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // 筛选条件
    const [filters, setFilters] = useState({
        vaccine_type: '',
        name: '',
        gender: '',
        province: '',
        city: '',
        district: '',
        startDate: '',
        endDate: '',
        relationship: ''
    });
    
    // 统计数据
    const [statistics, setStatistics] = useState(null);
    const [showStatistics, setShowStatistics] = useState(false);
    const [showFilters, setShowFilters] = useState(true); // 移动端筛选表单显示控制，默认展开
    const [isMobileView, setIsMobileView] = useState(false); // 检测是否为移动端视图
    
    // 在线会话
    const [onlineSessions, setOnlineSessions] = useState([]);
    const [showOnlineSessions, setShowOnlineSessions] = useState(false);
    const [loadingOnlineSessions, setLoadingOnlineSessions] = useState(false);
    const [closingSession, setClosingSession] = useState(null); // 正在挂断的会话UserId
    
    const hostIP = getHostIP();

    // 处理管理员登录（通过验证token有效性）
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);

        if (!adminTokenInput.trim()) {
            setLoginError('请输入管理员令牌');
            setLoginLoading(false);
            return;
        }

        try {
            // 通过调用管理接口来验证token有效性
            const response = await axios.get(`${hostIP}/api/admin/statistics`, {
                headers: {
                    'Authorization': `Bearer ${adminTokenInput.trim()}`
                },
                withCredentials: true,
                params: { limit: 1 } // 只获取少量数据用于验证
            });

            if (response.data.code === 200) {
                // token验证成功
                setAdminToken(adminTokenInput.trim());
                setIsAuthenticated(true);
                localStorage.setItem('adminToken', adminTokenInput.trim());
                setAdminTokenInput(''); // 清空输入框
            } else {
                setLoginError('管理员令牌无效');
            }
        } catch (error) {
            console.error('令牌验证失败:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                setLoginError('管理员令牌无效或已过期');
            } else {
                setLoginError('验证失败，请检查网络连接');
            }
        } finally {
            setLoginLoading(false);
        }
    };

    // 退出登录
    const handleLogout = () => {
        setIsAuthenticated(false);
        setAdminTokenInput('');
        setLoginError('');
        setAdminToken('');
        localStorage.removeItem('adminToken');
        setRecords([]);
        setSessionDetails(null);
        setSelectedSession(null);
        setStatistics(null);
        setShowStatistics(false);
        setOnlineSessions([]);
        setShowOnlineSessions(false);
    };

    // 创建带认证头的axios配置
    const getAuthConfig = () => ({
        headers: {
            'Authorization': `Bearer ${adminToken}`
        },
        withCredentials: true
    });

    // 检查认证状态
    useEffect(() => {
        const savedToken = localStorage.getItem('adminToken');
        if (savedToken) {
            setAdminToken(savedToken);
            setIsAuthenticated(true);
        }
    }, []);

    // 地区选项
    const locations = [
        { province: '安徽省', city: '池州市' },
        { province: '甘肃省', city: '兰州市' },
        { province: '浙江省', city: '' },
        { province: '浙江省', city: '杭州市' },
        { province: '山西省', city: '太原市' },
        { province: '云南省', city: '大理州' },
        { province: '江苏省', city: '启东县' },
        { province: '江苏省', city: '崇川区' },
        { province: '江苏省', city: '通州区' }
    ];
    
    // 获取聊天记录列表
    const fetchRecords = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                pageSize,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== '')
                )
            };
            
            const response = await axios.get(`${hostIP}/api/admin/chat-records`, {
                params,
                ...getAuthConfig()
            });
            
            if (response.data.code === 200) {
                setRecords(response.data.data.records);
                setTotal(response.data.data.total);
                setTotalPages(response.data.data.totalPages);
            }
        } catch (error) {
            console.error('获取聊天记录失败:', error);
            alert('获取聊天记录失败，请检查权限或网络连接');
        } finally {
            setLoading(false);
        }
    };
    
    // 获取会话详情
    const fetchSessionDetails = async (sessionId, vaccineType) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${hostIP}/api/admin/chat-records/${sessionId}`,
                {
                    ...getAuthConfig(),
                    params: vaccineType ? {vaccine_type: vaccineType} : undefined
                }
            );
            
            if (response.data.code === 200) {
                setSessionDetails(response.data.data);
                setSelectedSession(sessionId);
            }
        } catch (error) {
            console.error('获取会话详情失败:', error);
            alert('获取会话详情失败');
        } finally {
            setLoading(false);
        }
    };
    
    // 获取统计数据
    const fetchStatistics = async () => {
        try {
            const params = {
                vaccine_type: filters.vaccine_type || undefined,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined
            };
            
            const response = await axios.get(`${hostIP}/api/admin/statistics`, {
                params,
                ...getAuthConfig()
            });
            
            if (response.data.code === 200) {
                setStatistics(response.data.data);
                setShowStatistics(true);
            }
        } catch (error) {
            console.error('获取统计数据失败:', error);
            alert('获取统计数据失败');
        }
    };
    
    // 获取在线会话列表
    const fetchOnlineSessions = async () => {
        setLoadingOnlineSessions(true);
        try {
            const response = await axios.get(`${hostIP}/api/admin/list-sessions-of-uin`, {
                ...getAuthConfig()
            });
            
            if (response.data.code === 200 && response.data.data?.Payload?.Sessions) {
                setOnlineSessions(response.data.data.Payload.Sessions);
                setShowOnlineSessions(true);
            } else {
                alert('获取在线会话失败');
            }
        } catch (error) {
            console.error('获取在线会话失败:', error);
            alert('获取在线会话失败，请检查权限或网络连接');
        } finally {
            setLoadingOnlineSessions(false);
        }
    };
    
    // 挂断会话
    const closeVirtualSession = async (userId) => {
        if (!window.confirm(`确定要挂断用户 ${userId} 的会话吗？`)) {
            return;
        }
        
        setClosingSession(userId);
        try {
            const response = await axios.post(
                `${hostIP}/api/admin/close-virtual-session`,
                { UserId: userId },
                getAuthConfig()
            );
            
            if (response.data.code === 200) {
                alert('挂断成功');
                // 刷新在线会话列表
                await fetchOnlineSessions();
            } else {
                alert('挂断失败：' + (response.data.message || '未知错误'));
            }
        } catch (error) {
            console.error('挂断会话失败:', error);
            alert('挂断会话失败：' + (error.response?.data?.message || error.message || '网络错误'));
        } finally {
            setClosingSession(null);
        }
    };
    
    // 导出聊天记录
    const exportRecords = async () => {
        setExporting(true);
        try {
            const response = await axios.post(
                `${hostIP}/api/admin/export-chat-records`,
                Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== '')
                ),
                {
                    responseType: 'blob',
                    ...getAuthConfig()
                }
            );
            
            // 创建下载链接
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const filename = `chat-records-${new Date().toISOString().split('T')[0]}.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            alert('导出成功！');
        } catch (error) {
            console.error('导出失败:', error);
            alert('导出失败，请重试');
        } finally {
            setExporting(false);
        }
    };
    
    // 处理筛选条件变化
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1); // 重置到第一页
    };
    
    // 重置筛选条件
    const resetFilters = () => {
        setFilters({
            vaccine_type: '',
            name: '',
            gender: '',
            province: '',
            city: '',
            district: '',
            startDate: '',
            endDate: '',
            relationship: ''
        });
        setPage(1);
    };
    
    // 格式化日期时间
    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // 获取性别文本
    const getGenderText = (gender) => {
        return gender === 1 ? '男' : gender === 0 ? '女' : '未知';
    };
    
    // 检测移动端视图
    useEffect(() => {
        const checkMobileView = () => {
            setIsMobileView(window.innerWidth <= 768);
        };

        checkMobileView();
        window.addEventListener('resize', checkMobileView);

        return () => window.removeEventListener('resize', checkMobileView);
    }, []);

    // 页面加载时获取数据
    useEffect(() => {
        if (isAuthenticated) {
            fetchRecords();
        }
    }, [page, pageSize, isAuthenticated]);

    // 如果未认证，显示登录界面
    if (!isAuthenticated) {
        return (
            <div className={styles.loginContainer}>
                <div className={styles.loginCard}>
                    <div className={styles.loginHeader}>
                        <h1>🔐 管理员登录</h1>
                        <p>请输入管理员令牌以访问管理后台</p>
                    </div>

                    <form onSubmit={handleLogin} className={styles.loginForm}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="adminToken">管理员令牌</label>
                            <input
                                id="adminToken"
                                type="password"
                                value={adminTokenInput}
                                onChange={(e) => setAdminTokenInput(e.target.value)}
                                placeholder="请输入管理员令牌"
                                required
                                className={styles.loginInput}
                            />
                        </div>

                        {loginError && (
                            <div className={styles.errorMessage}>
                                {loginError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loginLoading}
                            className={styles.loginButton}
                        >
                            {loginLoading ? (
                                <>
                                    <span className={styles.spinner}></span>
                                    验证中...
                                </>
                            ) : (
                                '登录'
                            )}
                        </button>
                    </form>

                    <div className={styles.loginFooter}>
                        <p>请联系系统管理员获取令牌</p>
                        <p>令牌格式：Bearer eyJhbGciOiJIUzI1NiIs...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <h1>聊天记录管理</h1>
                    <button
                        onClick={handleLogout}
                        className={styles.logoutButton}
                    >
                        🚪 退出登录
                    </button>
                </div>
                <div className={styles.headerButtons}>
                    <button 
                        onClick={fetchOnlineSessions}
                        className={styles.onlineSessionsButton}
                        disabled={loadingOnlineSessions}
                    >
                        {loadingOnlineSessions ? '加载中...' : '📞 在线会话'}
                    </button>
                    <button 
                        onClick={fetchStatistics}
                        className={styles.statsButton}
                    >
                        📊 查看统计
                    </button>
                    <button 
                        onClick={exportRecords}
                        className={styles.exportButton}
                        disabled={exporting}
                    >
                        {exporting ? '导出中...' : '📥 导出Excel'}
                    </button>
                </div>
            </div>
            
            {/* 筛选区域 */}
            <div className={styles.filterSection}>
                <div className={styles.filterHeader}>
                    <h3>筛选条件</h3>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={styles.toggleFiltersButton}
                    >
                        {showFilters ? '收起筛选' : '展开筛选'} {showFilters ? '🔽' : '▶️'}
                    </button>
                </div>

                <div className={`${styles.filterContent} ${showFilters ? styles.filterContentExpanded : ''}`}>
                    <div className={styles.filterGrid}>
                    <div className={styles.filterItem}>
                        <label>应用类型：</label>
                        <select
                            value={filters.vaccine_type}
                            onChange={(e) => handleFilterChange('vaccine_type', e.target.value)}
                        >
                            <option value="">全部</option>
                            <option value="FLU">流感疫苗</option>
                            <option value="HPV">HPV疫苗</option>
                            <option value="RSV">RSV预防</option>
                            <option value="CRC">肠癌筛查</option>
                        </select>
                    </div>
                    
                    <div className={styles.filterItem}>
                        <label>姓名：</label>
                        <input
                            type="text"
                            placeholder="输入姓名搜索"
                            value={filters.name}
                            onChange={(e) => handleFilterChange('name', e.target.value)}
                        />
                    </div>
                    
                    <div className={styles.filterItem}>
                        <label>性别：</label>
                        <select
                            value={filters.gender}
                            onChange={(e) => handleFilterChange('gender', e.target.value)}
                        >
                            <option value="">全部</option>
                            <option value="1">男</option>
                            <option value="0">女</option>
                        </select>
                    </div>
                    
                    <div className={styles.filterItem}>
                        <label>省市：</label>
                        <select
                            value={`${filters.province} ${filters.city}`.trim()}
                            onChange={(e) => {
                                const selected = locations.find(
                                    loc => `${loc.province} ${loc.city}` === e.target.value
                                );
                                if (selected) {
                                    handleFilterChange('province', selected.province);
                                    handleFilterChange('city', selected.city);
                                } else {
                                    handleFilterChange('province', '');
                                    handleFilterChange('city', '');
                                }
                            }}
                        >
                            <option value="">全部</option>
                            {locations.map((loc, idx) => (
                                <option key={idx} value={`${loc.province} ${loc.city}`}>
                                    {loc.province} {loc.city}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className={styles.filterItem}>
                        <label>区县：</label>
                        <input
                            type="text"
                            placeholder="输入区县"
                            value={filters.district}
                            onChange={(e) => handleFilterChange('district', e.target.value)}
                        />
                    </div>
                    
                    {filters.vaccine_type === 'CRC' && (
                        <div className={styles.filterItem}>
                            <label>关系：</label>
                            <select
                                value={filters.relationship}
                                onChange={(e) => handleFilterChange('relationship', e.target.value)}
                            >
                                <option value="">全部</option>
                                <option value="本人">本人</option>
                                <option value="配偶">配偶</option>
                                <option value="子女">子女</option>
                                <option value="医生">医生</option>
                                <option value="其他">其他</option>
                            </select>
                        </div>
                    )}
                    
                    <div className={styles.filterItem}>
                        <label>开始日期：</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>
                    
                    <div className={styles.filterItem}>
                        <label>结束日期：</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>
                    </div>

                    <div className={styles.filterActions}>
                        <button onClick={fetchRecords} className={styles.searchButton}>
                            🔍 搜索
                        </button>
                        <button onClick={resetFilters} className={styles.resetButton}>
                            🔄 重置
                        </button>
                    </div>
                </div>
            </div>
            
            {/* 在线会话弹窗 */}
            {showOnlineSessions && (
                <div className={styles.modal} onClick={() => setShowOnlineSessions(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>在线会话列表</h2>
                            <button 
                                className={styles.closeButton}
                                onClick={() => setShowOnlineSessions(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.onlineSessionsList}>
                            {onlineSessions.length === 0 ? (
                                <div className={styles.empty}>暂无在线会话</div>
                            ) : (
                                <div className={styles.sessionsTable}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>用户名</th>
                                                <th>用户ID</th>
                                                <th>会话ID</th>
                                                <th>会话状态</th>
                                                <th>是否已开始</th>
                                                <th>虚拟人类型</th>
                                                <th>操作</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {onlineSessions.map((session, index) => (
                                                <tr key={index}>
                                                    <td className={styles.userNameCell}>
                                                        <strong>{session.UserName || '-'}</strong>
                                                    </td>
                                                    <td>{session.UserId}</td>
                                                    <td className={styles.sessionIdCell}>{session.SessionId}</td>
                                                    <td>
                                                        <span className={session.SessionStatus === 1 ? styles.statusActive : styles.statusInactive}>
                                                            {session.SessionStatus === 1 ? '进行中' : '已结束'}
                                                        </span>
                                                    </td>
                                                    <td>{session.IsSessionStarted ? '是' : '否'}</td>
                                                    <td>{session.VirtualmanType || '-'}</td>
                                                    <td>
                                                        <button
                                                            onClick={() => closeVirtualSession(session.UserId)}
                                                            className={styles.hangupButton}
                                                            disabled={closingSession === session.UserId}
                                                        >
                                                            {closingSession === session.UserId ? '挂断中...' : '挂断'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button 
                                onClick={fetchOnlineSessions}
                                className={styles.refreshButton}
                                disabled={loadingOnlineSessions}
                            >
                                🔄 刷新
                            </button>
                            <button 
                                onClick={() => setShowOnlineSessions(false)}
                                className={styles.closeModalButton}
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* 统计数据弹窗 */}
            {showStatistics && statistics && (
                <div className={styles.modal} onClick={() => setShowStatistics(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>统计数据</h2>
                            <button 
                                className={styles.closeButton}
                                onClick={() => setShowStatistics(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>总会话数</div>
                                <div className={styles.statValue}>{statistics.totalSessions}</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>总消息数</div>
                                <div className={styles.statValue}>{statistics.totalMessages}</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>平均消息数</div>
                                <div className={styles.statValue}>
                                    {statistics.avgMessagesPerSession.toFixed(1)}
                                </div>
                            </div>
                        </div>
                        
                        {statistics.genderDistribution && (
                            <div className={styles.statsSection}>
                                <h3>性别分布</h3>
                                <div className={styles.distributionBar}>
                                    <div 
                                        className={styles.maleBar}
                                        style={{
                                            width: `${(statistics.genderDistribution.male / 
                                                (statistics.genderDistribution.male + statistics.genderDistribution.female)) * 100}%`
                                        }}
                                    >
                                        男 {statistics.genderDistribution.male}
                                    </div>
                                    <div 
                                        className={styles.femaleBar}
                                        style={{
                                            width: `${(statistics.genderDistribution.female / 
                                                (statistics.genderDistribution.male + statistics.genderDistribution.female)) * 100}%`
                                        }}
                                    >
                                        女 {statistics.genderDistribution.female}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {statistics.regionDistribution && statistics.regionDistribution.length > 0 && (
                            <div className={styles.statsSection}>
                                <h3>地区分布（Top 10）</h3>
                                <div className={styles.regionList}>
                                    {statistics.regionDistribution.slice(0, 10).map((region, idx) => (
                                        <div key={idx} className={styles.regionItem}>
                                            <span>{region.province} {region.city}</span>
                                            <span className={styles.regionCount}>{region.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* 记录列表 */}
            <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                    <h3>聊天记录列表</h3>
                    <div className={styles.pagination}>
                        <span>共 {total} 条记录</span>
                        <span>|</span>
                        <span>每页显示</span>
                        <select 
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className={styles.pageSizeSelect}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                        <span>条</span>
                    </div>
                </div>
                
                {loading ? (
                    <div className={styles.loading}>加载中...</div>
                ) : records.length === 0 ? (
                    <div className={styles.empty}>暂无数据</div>
                ) : isMobileView ? (
                    // 移动端卡片式布局
                    <div className={styles.mobileCards}>
                        {records.map((record) => (
                            <div key={record.id} className={styles.recordCard}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardTitle}>
                                        <span className={`${styles.appBadge} ${styles[getVaccineClass(record.vaccineType)]}`}>
                                            {getVaccineLabel(record.vaccineType)}
                                        </span>
                                        <span className={styles.recordId}>#{record.id}</span>
                                    </div>
                                    <div className={styles.cardTime}>
                                        {formatDateTime(record.createdAt)}
                                    </div>
                                </div>

                                <div className={styles.cardContent}>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>姓名：</span>
                                        <span className={styles.cardValue}>{record.name}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>性别：</span>
                                        <span className={styles.cardValue}>{getGenderText(record.gender)}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>手机后4位：</span>
                                        <span className={styles.cardValue}>{record.phone}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>地区：</span>
                                        <span className={styles.cardValue}>
                                            {record.province} {record.city}<br/>
                                            <small style={{color: '#909399'}}>{record.district}</small>
                                        </span>
                                    </div>
                                    {record.hospital && (
                                        <div className={styles.cardRow}>
                                            <span className={styles.cardLabel}>村/社区：</span>
                                            <span className={styles.cardValue}>{record.hospital}</span>
                                        </div>
                                    )}
                                    {record.relationship && (
                                        <div className={styles.cardRow}>
                                            <span className={styles.cardLabel}>关系：</span>
                                            <span className={styles.cardValue}>{record.relationship}</span>
                                        </div>
                                    )}
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>消息数：</span>
                                        <span className={styles.cardValue}>{record.messageCount}</span>
                                    </div>
                                </div>

                                <div className={styles.cardActions}>
                                    <button
                                        onClick={() => fetchSessionDetails(record.sessionId, record.vaccineType)}
                                        className={styles.viewButton}
                                    >
                                        查看详情
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // 桌面端表格布局
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>疫苗/项目</th>
                                    <th>姓名</th>
                                    <th>性别</th>
                                    <th>手机后4位</th>
                                    <th>地区</th>
                                    <th>村/社区</th>
                                    <th>关系</th>
                                    <th>消息数</th>
                                    <th>创建时间</th>
                                    <th>最后更新</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((record) => (
                                    <tr key={record.id}>
                                        <td>{record.id}</td>
                                        <td>
                                            <span className={`${styles.appBadge} ${styles[getVaccineClass(record.vaccineType)]}`}>
                                                {getVaccineLabel(record.vaccineType)}
                                            </span>
                                        </td>
                                        <td>{record.name}</td>
                                        <td>{getGenderText(record.gender)}</td>
                                        <td>{record.phone}</td>
                                        <td>
                                            {record.province} {record.city}<br/>
                                            {record.district}
                                        </td>
                                        <td>{record.hospital || '-'}</td>
                                        <td>{record.relationship || '-'}</td>
                                        <td>{record.messageCount}</td>
                                        <td>{formatDateTime(record.createdAt)}</td>
                                        <td>{formatDateTime(record.updatedAt)}</td>
                                        <td>
                                            <button
                                                onClick={() => fetchSessionDetails(record.sessionId, record.vaccineType)}
                                                className={styles.viewButton}
                                            >
                                                查看详情
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                {/* 分页控件 */}
                {totalPages > 1 && (
                    <div className={styles.paginationControls}>
                        <button
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                            className={styles.pageButton}
                        >
                            首页
                        </button>
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page === 1}
                            className={styles.pageButton}
                        >
                            上一页
                        </button>
                        
                        <div className={styles.pageNumbers}>
                            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 10) {
                                    pageNum = i + 1;
                                } else if (page <= 5) {
                                    pageNum = i + 1;
                                } else if (page >= totalPages - 4) {
                                    pageNum = totalPages - 9 + i;
                                } else {
                                    pageNum = page - 4 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setPage(pageNum)}
                                        className={`${styles.pageButton} ${page === pageNum ? styles.active : ''}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page === totalPages}
                            className={styles.pageButton}
                        >
                            下一页
                        </button>
                        <button
                            onClick={() => setPage(totalPages)}
                            disabled={page === totalPages}
                            className={styles.pageButton}
                        >
                            末页
                        </button>
                        
                        <span className={styles.pageInfo}>
                            第 {page} / {totalPages} 页
                        </span>
                    </div>
                )}
            </div>
            
            {/* 会话详情弹窗 */}
            {selectedSession && sessionDetails && (
                <div className={styles.modal} onClick={() => setSelectedSession(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>会话详情</h2>
                            <button 
                                className={styles.closeButton}
                                onClick={() => setSelectedSession(null)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className={styles.userInfo}>
                            <h3>用户信息</h3>
                            <div className={styles.infoGrid}>
                                {sessionDetails.vaccineType && (
                                    <div><strong>疫苗/项目：</strong>{getVaccineLabel(sessionDetails.vaccineType)}</div>
                                )}
                                <div><strong>姓名：</strong>{sessionDetails.userInfo.name}</div>
                                <div><strong>性别：</strong>{getGenderText(sessionDetails.userInfo.gender)}</div>
                                <div><strong>手机后4位：</strong>{sessionDetails.userInfo.phone}</div>
                                <div><strong>地区：</strong>
                                    {sessionDetails.userInfo.province} {sessionDetails.userInfo.city} {sessionDetails.userInfo.district}
                                </div>
                                {sessionDetails.userInfo.hospital && (
                                    <div><strong>村/社区：</strong>{sessionDetails.userInfo.hospital}</div>
                                )}
                                {sessionDetails.userInfo.relationship && (
                                    <div><strong>关系：</strong>{sessionDetails.userInfo.relationship}</div>
                                )}
                            </div>
                        </div>
                        
                        <div className={styles.messages}>
                            <h3>聊天记录</h3>
                            <div className={styles.messageList}>
                                {sessionDetails.messages.map((msg) => (
                                    <div 
                                        key={msg.id}
                                        className={`${styles.message} ${styles[msg.role]}`}
                                    >
                                        <div className={styles.messageHeader}>
                                            <span className={styles.messageRole}>
                                                {msg.role === 'user' ? '👤 用户' : '🤖 助手'}
                                            </span>
                                            <span className={styles.messageTime}>
                                                {formatDateTime(msg.timestamp)}
                                            </span>
                                        </div>
                                        <div className={styles.messageContent}>
                                            {msg.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
